/**
 * Corrections tab (Layer 2 — F-correction).
 *
 * Propose → approve/reject workflow over a batch's input data. Two correction
 * types: `substitution` (replace an estimated/bad value) and `exclusion` (drop
 * a reading entirely). The BE rejects an over-threshold substitution with
 * SUBSTITUTION_OVER_THRESHOLD — the error surfaces in the propose drawer and
 * the operator switches to an exclusion (locked decision #9: no re-enable
 * affordance once over threshold).
 *
 * Role gating mirrors the BE: propose is open to any non-auditor role;
 * approve/reject are reviewer/admin. The server is authoritative — the FE
 * hides the obviously-forbidden controls and shows server rejections inline.
 */

import { isApiError } from "@compliance/domain";
import {
  Alert,
  Badge,
  Box,
  Button,
  CloseButton,
  Divider,
  Drawer,
  Group,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertCircle,
  IconArrowRight,
  IconCheck,
  IconChevronRight,
  IconDatabase,
  IconInfoCircle,
  IconPencilPlus,
  IconWand,
  IconX,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  useApproveCorrection,
  useBatchCorrections,
  useProposeCorrection,
  useRejectCorrection,
} from "../../api/hooks";
import type {
  BatchDetail,
  BatchState,
  CorrectionRecord,
  CorrectionStatus,
  CorrectionType,
  ProposeCorrectionRequest,
} from "../../api/types";
import { useHasRole } from "../../auth";
import { DetailRow } from "../../components/DetailRow";
import { SubstitutionMeterCard } from "../../components/SubstitutionMeterCard";
import {
  formatPacificFriendly,
  formatPacificFriendlyRange,
  pacificLocalToInstant,
} from "../../lib/datetime";
import { formatApiError } from "../../lib/errors";
import { invalidateBatchData, refreshInBackground } from "../../lib/invalidate";
import { CorrectionValuePicker } from "./CorrectionValuePicker";
import type { PickedDatum } from "./correctionPick";

const TYPE_LABEL: Record<CorrectionType, string> = {
  substitution: "Substitution",
  exclusion: "Exclusion",
};

const STATUS_COLOR: Record<CorrectionStatus, string> = {
  proposed: "yellow",
  approved: "teal",
  rejected: "gray",
};

// Every state. Corrections used to be refused past sign-off, because the
// signature named a set of figures a later correction would silently invalidate.
// There is no signature and no freeze, so correcting an approved batch is just
// correcting a batch. Kept as an explicit list rather than collapsed away so an
// unknown future state defaults to NOT editable.
const CORRECTION_EDITABLE_STATES: ReadonlyArray<BatchState> = [
  "Open",
  "PendingApproval",
  "Approved",
];

/**
 * DetailRow props for an actor field (BIO-257): prefer the backend-resolved
 * display name, then email, else fall back to the raw UUID. Names/emails render
 * plain; the UUID stays monospace.
 */
export function actorRow(
  displayName: string | null | undefined,
  email: string | null | undefined,
  uuid: string | null,
): { value: string | null; mono: boolean } {
  const resolved = displayName ?? email ?? null;
  return resolved ? { value: resolved, mono: false } : { value: uuid, mono: true };
}

/**
 * Why corrections are unavailable.
 *
 * One message now, and it is unreachable for any state this build knows: every
 * one of them is correctable. It survives as the honest answer if the backend
 * ever reports a state this bundle has not heard of — the allow-list defaults
 * such a state to NOT editable rather than offering a correction that 4xxs.
 */
const NOT_CORRECTABLE = "Corrections aren't available for this batch's current state.";

export function CorrectionsTab({ batch }: { batch: BatchDetail }) {
  const correctionsQ = useBatchCorrections(batch.batch_id);
  const canPropose = useHasRole("operations", "reviewer", "admin");
  const [proposeOpen, setProposeOpen] = useState(false);
  const [selected, setSelected] = useState<CorrectionRecord | null>(null);

  const corrections = correctionsQ.data ?? [];
  // Corrections are only valid in pre-signature states (see
  const editable = CORRECTION_EDITABLE_STATES.includes(batch.state);

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Stack gap={4}>
          <Title order={4}>Corrections</Title>
          <Text size="sm" c="dimmed">
            Substitutions and exclusions to this batch's input data, recorded in the RFS report and
            audit log. Rows marked{" "}
            <Text span fw={600}>
              Affects figures
            </Text>{" "}
            change the filed numbers when approved; the rest are recorded for disclosure only. Click
            a row for detail.
          </Text>
        </Stack>
        <Button
          leftSection={<IconPencilPlus size={16} />}
          onClick={() => setProposeOpen(true)}
          disabled={!canPropose || !editable}
        >
          Propose correction
        </Button>
      </Group>

      <SubstitutionMeterCard batchId={batch.batch_id} />

      {!editable ? (
        <Alert color="gray" variant="light" icon={<IconAlertCircle size={16} />}>
          <Text size="sm">{NOT_CORRECTABLE}</Text>
        </Alert>
      ) : null}

      {correctionsQ.isError ? (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
          <Text size="sm">Couldn't load corrections: {formatApiError(correctionsQ.error)}</Text>
        </Alert>
      ) : null}

      <Paper withBorder radius="md">
        <ScrollArea>
          <Table verticalSpacing="sm" horizontalSpacing="md" fz="sm" highlightOnHover miw={860}>
            <Table.Thead style={{ background: "var(--mantine-color-gray-0)" }}>
              <Table.Tr>
                <Table.Th style={{ width: 120 }}>Type</Table.Th>
                <Table.Th style={{ width: 110 }}>Status</Table.Th>
                <Table.Th>Source</Table.Th>
                <Table.Th>Before → After</Table.Th>
                <Table.Th style={{ width: 220 }}>Proposed</Table.Th>
                <Table.Th style={{ width: 32 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {corrections.map((c) => (
                <Table.Tr
                  key={c.correction_id}
                  onClick={() => setSelected(c)}
                  style={{ cursor: "pointer" }}
                >
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Badge variant="default" size="sm">
                        {TYPE_LABEL[c.correction_type]}
                      </Badge>
                      {/* BIO-308 #5b: which rows actually move the filed
                          numbers. Without this the table would need one blanket
                          sentence, and either wording is wrong for half the rows. */}
                      {c.affects_figures === true ? (
                        <Tooltip
                          label="Approving this recomputes the batch's filed figures"
                          position="top"
                        >
                          <Badge color="orange" variant="light" size="sm">
                            Affects figures
                          </Badge>
                        </Tooltip>
                      ) : null}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <StatusBadge status={c.status} />
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {c.source_identifier ?? "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <BeforeAfter before={c.before_value} after={c.after_value} />
                  </Table.Td>
                  <Table.Td ff="monospace" fz="xs">
                    {formatPacificFriendly(c.proposed_at)}
                  </Table.Td>
                  <Table.Td>
                    <ThemeIcon variant="subtle" color="gray" size="sm">
                      <IconChevronRight size={14} />
                    </ThemeIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>

        {correctionsQ.isSuccess && corrections.length === 0 ? (
          <Stack align="center" py="xl" gap="xs">
            <ThemeIcon variant="light" color="gray" size={40} radius="xl">
              <IconWand size={20} />
            </ThemeIcon>
            <Text c="dimmed" size="sm">
              No corrections proposed for this batch.
            </Text>
          </Stack>
        ) : null}
      </Paper>

      <ProposeDrawer batch={batch} opened={proposeOpen} onClose={() => setProposeOpen(false)} />
      <CorrectionDrawer
        correction={selected}
        batchId={batch.batch_id}
        batchState={batch.state}
        onClose={() => setSelected(null)}
      />
    </Stack>
  );
}

function StatusBadge({ status }: { status: CorrectionStatus }) {
  return (
    <Badge color={STATUS_COLOR[status]} variant="light" size="sm">
      {status}
    </Badge>
  );
}

function BeforeAfter({ before, after }: { before: string | null; after: string | null }) {
  if (!before && !after) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }
  return (
    <Group gap={6} wrap="nowrap">
      <Text size="sm" ff="monospace" td={after ? "line-through" : undefined} c="dimmed">
        {before ?? "∅"}
      </Text>
      <IconArrowRight size={13} />
      <Text size="sm" ff="monospace" fw={500}>
        {after ?? "excluded"}
      </Text>
    </Group>
  );
}

// ── Propose drawer ──────────────────────────────────────────────────────────

function ProposeDrawer({
  batch,
  opened,
  onClose,
}: {
  batch: BatchDetail;
  opened: boolean;
  onClose: () => void;
}) {
  const propose = useProposeCorrection();
  const queryClient = useQueryClient();

  const [type, setType] = useState<CorrectionType>("substitution");
  const [source, setSource] = useState("");
  // Native datetime-local values ("YYYY-MM-DDTHH:mm"), entered as Pacific
  // wall-clock and converted to offset-aware instants on submit.
  const [periodStartLocal, setPeriodStartLocal] = useState("");
  const [periodEndLocal, setPeriodEndLocal] = useState("");
  const [beforeValue, setBeforeValue] = useState("");
  const [afterValue, setAfterValue] = useState("");
  const [reason, setReason] = useState("");
  // "Select from imported data" picker + a note showing what was filled.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedLabel, setPickedLabel] = useState<string | null>(null);
  // Correction context of the picked datum: set when the pick landed on an
  // already-corrected cell (correcting a correction) or one with pending
  // proposals. Cleared with pickedLabel on any hand-edit — a changed source or
  // before-value means the warning no longer describes what will be submitted.
  const [pickedContext, setPickedContext] = useState<Pick<
    PickedDatum,
    "correctedBy" | "pendingCorrectionIds"
  > | null>(null);

  const sourceValid = source.trim().length > 0;
  const reasonValid = reason.trim().length >= 3;
  // Substitution must land on a value; exclusion drops the reading entirely.
  const afterValid = type === "exclusion" || afterValue.trim().length > 0;

  // The picker yields a naive PT wall-clock; convert to the offset-aware instant
  // the BE expects. A null conversion means an incomplete/invalid pick.
  const startProvided = periodStartLocal.trim().length > 0;
  const endProvided = periodEndLocal.trim().length > 0;
  const periodStartIso = pacificLocalToInstant(periodStartLocal);
  const periodEndIso = pacificLocalToInstant(periodEndLocal);
  // A substitution MUST carry a valid period start (the BE only counts
  // substitution days — and runs the TGP over-threshold guard — when
  // period_start is present). A supplied start on either type must be valid.
  const periodStartValid =
    type === "substitution" ? periodStartIso !== null : !startProvided || periodStartIso !== null;
  // A supplied end must convert cleanly and not precede the start — an inverted
  // range shifts/empties the BE's day window.
  const periodEndValid =
    !endProvided ||
    (periodEndIso !== null &&
      (periodStartIso === null || Date.parse(periodEndIso) >= Date.parse(periodStartIso)));
  const canSubmit =
    sourceValid &&
    reasonValid &&
    afterValid &&
    periodStartValid &&
    periodEndValid &&
    !propose.isPending;

  const reset = () => {
    setType("substitution");
    setSource("");
    setPeriodStartLocal("");
    setPeriodEndLocal("");
    setBeforeValue("");
    setAfterValue("");
    setReason("");
    setPickedLabel(null);
    setPickedContext(null);
    propose.reset();
  };

  const close = () => {
    if (propose.isPending) return;
    onClose();
  };

  // A pick fills the source, the period start (the gas-day at PT midnight for a
  // daily pick, the exact hour for an hourly pick), and the Before value with the
  // actual reading — the EFFECTIVE value when an approved correction underlies
  // the pick (correcting a correction). The After value stays typed.
  const onPickImported = (d: PickedDatum) => {
    setSource(d.source_identifier);
    if (d.period_local) setPeriodStartLocal(d.period_local);
    setBeforeValue(d.value);
    setPickedLabel(d.label);
    setPickedContext(
      d.correctedBy || d.pendingCorrectionIds?.length
        ? { correctedBy: d.correctedBy, pendingCorrectionIds: d.pendingCorrectionIds }
        : null,
    );
    setPickerOpen(false);
  };

  const onSubmit = async () => {
    const input: ProposeCorrectionRequest = {
      correction_type: type,
      source_identifier: source.trim(),
      period_start: periodStartIso ?? undefined,
      period_end: periodEndIso ?? undefined,
      before_value: beforeValue.trim() || undefined,
      after_value: type === "exclusion" ? undefined : afterValue.trim() || undefined,
    };
    try {
      await propose.mutateAsync({ batchId: batch.batch_id, input, reason: reason.trim() });
      notifications.show({
        title: "Correction proposed",
        message: `${TYPE_LABEL[type]} on ${input.source_identifier} — awaiting review.`,
        color: "teal",
        icon: <IconCheck size={18} />,
      });
      reset();
      onClose();
      // Proposing writes an audit event; refresh the batch surface (not just
      // the corrections table) so the Audit tab reflects it. After the close —
      // the drawer has nothing to learn from the refetch.
      refreshInBackground(invalidateBatchData(queryClient, batch.batch_id));
    } catch {
      // Failure is captured in the mutation's `.error` and rendered inline
      // (ApiError or unexpected network/TypeError). Don't rethrow — this runs in
      // a click handler, so a rethrow would escape as an unhandled rejection.
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={close}
      onExitTransitionEnd={reset}
      position="right"
      size="lg"
      padding="lg"
      title={
        <Group gap="sm">
          <ThemeIcon variant="light" radius="md">
            <IconPencilPlus size={18} />
          </ThemeIcon>
          <Stack gap={0}>
            <Title order={4} lh={1.1}>
              Propose correction
            </Title>
            <Text size="xs" c="dimmed">
              {batch.batch_id}
            </Text>
          </Stack>
        </Group>
      }
    >
      <Stack gap="md">
        {propose.error ? <DrawerError error={propose.error} verb="Propose" /> : null}

        {pickedContext?.correctedBy ? (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm">
              You're correcting a correction. The Before value is the currently-effective corrected
              value (correction{" "}
              <Text span ff="monospace" size="sm">
                {pickedContext.correctedBy.correction_id.slice(0, 8)}
              </Text>
              ; original DB value{" "}
              <Text span ff="monospace" size="sm">
                {pickedContext.correctedBy.db_value ?? "—"}
              </Text>
              ). If approved, this proposal supersedes it in the data view; both remain in the RFS
              substitution log as separate disclosed events.
            </Text>
          </Alert>
        ) : null}
        {pickedContext?.pendingCorrectionIds?.length ? (
          <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm">
              A pending correction already targets this datum — consider rejecting it first to avoid
              duplicate proposals.
            </Text>
          </Alert>
        ) : null}

        <Select
          label="Type"
          data={[
            { value: "substitution", label: "Substitution — replace a value" },
            { value: "exclusion", label: "Exclusion — drop the reading" },
          ]}
          value={type}
          onChange={(v) => v && setType(v as CorrectionType)}
          allowDeselect={false}
        />

        <Stack gap={6}>
          <Group justify="space-between" align="center">
            <Text size="sm" fw={500}>
              Source &amp; before value
            </Text>
            <Button
              size="xs"
              variant="light"
              leftSection={<IconDatabase size={14} />}
              onClick={() => setPickerOpen(true)}
            >
              Select from imported data
            </Button>
          </Group>
          <TextInput
            label="Source identifier"
            description="The data point being corrected (meter tag, upload row key, etc.)."
            placeholder="e.g. ME-001 or pge_statement:row-42"
            value={source}
            onChange={(e) => {
              setSource(e.currentTarget.value);
              setPickedLabel(null);
              setPickedContext(null);
            }}
            required
          />
          {pickedLabel ? (
            <Text size="xs" c="teal">
              Filled from imported data: {pickedLabel}. You can still edit any field.
            </Text>
          ) : null}
        </Stack>

        <Group grow align="flex-start">
          <TextInput
            type="datetime-local"
            label="Period start"
            description={
              type === "substitution"
                ? "Required for substitutions. Pacific time."
                : "Optional. Pacific time."
            }
            value={periodStartLocal}
            onChange={(e) => setPeriodStartLocal(e.currentTarget.value)}
            required={type === "substitution"}
            error={startProvided && periodStartIso === null ? "Pick a valid date & time" : null}
          />
          <TextInput
            type="datetime-local"
            label="Period end"
            description="Optional. Pacific time."
            value={periodEndLocal}
            onChange={(e) => setPeriodEndLocal(e.currentTarget.value)}
            error={
              endProvided && !periodEndValid
                ? "Pick a valid date & time at or after the start"
                : null
            }
          />
        </Group>

        <Group grow align="flex-start">
          <TextInput
            label="Before value"
            description="Optional — the current/estimated value."
            placeholder="e.g. 1234.5"
            value={beforeValue}
            onChange={(e) => {
              setBeforeValue(e.currentTarget.value);
              setPickedLabel(null);
              setPickedContext(null);
            }}
          />
          {type === "substitution" ? (
            <TextInput
              label="After value"
              description="The substituted value."
              placeholder="e.g. 1300.0"
              value={afterValue}
              onChange={(e) => setAfterValue(e.currentTarget.value)}
              required
            />
          ) : (
            <Box />
          )}
        </Group>

        <Textarea
          label="Reason"
          description="Recorded in the audit log as the proposal reason. Minimum 3 characters."
          minRows={3}
          maxRows={6}
          autosize
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          error={reason.length > 0 && !reasonValid ? "Reason must be at least 3 characters" : null}
        />

        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={close} disabled={propose.isPending}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} loading={propose.isPending} onClick={onSubmit}>
            Propose
          </Button>
        </Group>

        <CorrectionValuePicker
          opened={pickerOpen}
          batch={batch}
          onPick={onPickImported}
          onClose={() => setPickerOpen(false)}
        />
      </Stack>
    </Drawer>
  );
}

// ── Detail + decision drawer ────────────────────────────────────────────────

function CorrectionDrawer({
  correction,
  batchId,
  batchState,
  onClose,
}: {
  correction: CorrectionRecord | null;
  batchId: string;
  batchState: BatchState;
  onClose: () => void;
}) {
  const canDecide = useHasRole("reviewer", "admin");
  const approve = useApproveCorrection();
  const reject = useRejectCorrection();
  const queryClient = useQueryClient();
  const [note, setNote] = useState("");

  const inFlight = approve.isPending || reject.isPending;
  const isPending = correction?.status === "proposed";
  const noteValid = note.trim().length >= 3;
  // False for every state this build knows — the guard is left standing for an
  // unrecognised one, where Approve is disabled and Reject stays allowed (it
  // just dismisses a stale proposal without changing figures).
  const locked = !CORRECTION_EDITABLE_STATES.includes(batchState);

  // Approving a correction on a source the calc reads recomputes the batch
  // server-side (BIO-308 #5b), so the figures, the reports' staleness grading and
  // the audit trail can all move. Corrections on other sources stay
  // disclosure-only. Either way we refresh the whole batch surface.
  const invalidate = () => refreshInBackground(invalidateBatchData(queryClient, batchId));

  // Reset both mutation states + the note field. Shared by the cancel/dismiss
  // path and the success path so a decision never leaves stale mutation state
  // (error/data) behind for the next correction opened in this drawer.
  const resetDecision = () => {
    setNote("");
    approve.reset();
    reject.reset();
  };

  const close = () => {
    if (inFlight) return;
    resetDecision();
    onClose();
  };

  const decide = async (verb: "approve" | "reject") => {
    if (!correction) return;
    const mutation = verb === "approve" ? approve : reject;
    const trimmed = note.trim();
    // Approve and reject are both audit-critical mutations, so the reviewer must
    // enter the reason — never synthesize a placeholder. The same text is sent
    // as X-Reason and recorded as the decision note. The button gate (noteValid)
    // keeps us from reaching here with a blank note.
    try {
      await mutation.mutateAsync({
        correctionId: correction.correction_id,
        note: trimmed,
        reason: trimmed,
      });
      notifications.show({
        title: verb === "approve" ? "Correction approved" : "Correction rejected",
        message: correction.source_identifier ?? correction.correction_id,
        color: verb === "approve" ? "teal" : "gray",
        icon: verb === "approve" ? <IconCheck size={18} /> : <IconX size={18} />,
      });
      resetDecision();
      onClose();
      invalidate();
    } catch {
      // Failure is captured in the mutation's `.error` and rendered inline
      // (ApiError or unexpected network/TypeError). Don't rethrow — this runs in
      // a click handler, so a rethrow would escape as an unhandled rejection.
    }
  };

  return (
    <Drawer
      opened={correction !== null}
      onClose={close}
      position="right"
      size="md"
      withCloseButton={false}
      padding="lg"
    >
      {correction ? (
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Stack gap={4}>
              <Group gap="xs">
                <Badge variant="default" size="sm">
                  {TYPE_LABEL[correction.correction_type]}
                </Badge>
                <StatusBadge status={correction.status} />
              </Group>
              <Title order={4} mt={4}>
                {correction.source_identifier ?? "(no source)"}
              </Title>
            </Stack>
            <CloseButton onClick={close} aria-label="Close" />
          </Group>

          <Divider />

          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
              Change
            </Text>
            <BeforeAfter before={correction.before_value} after={correction.after_value} />
            <Alert
              color={correction?.affects_figures === true ? "orange" : "blue"}
              variant="light"
              icon={<IconInfoCircle size={16} />}
              mt={4}
              p="xs"
            >
              <Text size="xs">
                {correction?.affects_figures === true
                  ? "Approving this recomputes the batch. The correction is applied to the affected gas-days before the Token MMBtu is summed, so Token / Eligible MMBtu, RF Gallons and D3 RINs can all move. It is also recorded in the RFS substitution log and the audit trail."
                  : "This is a disclosed adjustment: approving it records the substitution/exclusion in the RFS report and audit trail. This source has no per-day series behind it, so the computed figures do not change — to change them, re-upload the source data."}
              </Text>
            </Alert>
          </Stack>

          <DetailRow
            label="Period"
            value={formatPacificFriendlyRange(correction.period_start, correction.period_end)}
            mono
          />
          <DetailRow
            label="Proposed by"
            {...actorRow(
              correction.proposed_by_display_name,
              correction.proposed_by_email,
              correction.proposed_by,
            )}
          />
          <DetailRow
            label="Proposed at"
            value={formatPacificFriendly(correction.proposed_at)}
            mono
          />
          <DetailRow label="Proposal reason" value={correction.proposal_reason} />

          {correction.status !== "proposed" ? (
            <>
              <Divider />
              <DetailRow
                label={correction.status === "approved" ? "Approved by" : "Rejected by"}
                {...actorRow(
                  correction.decided_by_display_name,
                  correction.decided_by_email,
                  correction.decided_by,
                )}
              />
              <DetailRow
                label={correction.status === "approved" ? "Approved at" : "Rejected at"}
                value={formatPacificFriendly(correction.decided_at)}
                mono
              />
              <DetailRow label="Decision note" value={correction.decision_note} />
            </>
          ) : null}

          {isPending && canDecide ? (
            <>
              <Divider />
              {locked ? (
                <Alert color="gray" variant="light" icon={<IconAlertCircle size={16} />}>
                  <Text size="sm">{NOT_CORRECTABLE} You can still reject this proposal.</Text>
                </Alert>
              ) : null}
              {approve.error || reject.error ? (
                <DrawerError error={approve.error ?? reject.error} verb="Decision" />
              ) : null}
              <Textarea
                label="Decision note"
                description="Recorded on the correction and as the audit reason. Minimum 3 characters."
                minRows={2}
                maxRows={5}
                autosize
                value={note}
                onChange={(e) => setNote(e.currentTarget.value)}
                error={note.length > 0 && !noteValid ? "Minimum 3 characters" : null}
              />
              <Group justify="flex-end" gap="xs">
                <Button
                  variant="default"
                  color="red"
                  leftSection={<IconX size={16} />}
                  onClick={() => decide("reject")}
                  loading={reject.isPending}
                  disabled={!noteValid || inFlight}
                >
                  Reject
                </Button>
                <Button
                  color="teal"
                  leftSection={<IconCheck size={16} />}
                  onClick={() => decide("approve")}
                  loading={approve.isPending}
                  disabled={!noteValid || locked || inFlight}
                >
                  Approve
                </Button>
              </Group>
            </>
          ) : isPending && !canDecide ? (
            <>
              <Divider />
              <Text size="xs" c="dimmed">
                Approving or rejecting a correction requires Compliance Reviewer or Admin role.
              </Text>
            </>
          ) : null}
        </Stack>
      ) : null}
    </Drawer>
  );
}

function DrawerError({ error, verb }: { error: unknown; verb: string }) {
  const overThreshold = isApiError(error) && error.code === "SUBSTITUTION_OVER_THRESHOLD";
  const title = isApiError(error) ? `${verb} failed (${error.code})` : `${verb} failed`;
  const message = isApiError(error) ? error.message : ((error as Error)?.message ?? String(error));
  return (
    <Alert
      color={overThreshold ? "orange" : "red"}
      variant="light"
      icon={<IconAlertCircle size={16} />}
      title={title}
    >
      <Text size="sm">{message}</Text>
      {overThreshold ? (
        <Text size="sm" mt={4}>
          The substitution exceeds the allowed threshold. Propose an exclusion instead.
        </Text>
      ) : null}
    </Alert>
  );
}
